/**
 * API hooks for Persona CRUD operations
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@lib/auth';
import type { Persona, ApiPersonaResponse, ApiPersonaCreate, ApiPersonaUpdate } from '../types';
import { toApiPersona, fromApiPersona } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// =============================================================================
// usePersonas - Fetch all personas
// =============================================================================

interface UsePersonasReturn {
  personas: Persona[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePersonas(): UsePersonasReturn {
  const { getAccessToken } = useAuthContext();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPersonas = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/personas`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch personas: ${response.status}`);
      }

      const data: ApiPersonaResponse[] = await response.json();
      setPersonas(data.map(fromApiPersona));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch personas');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  return { personas, isLoading, error, refetch: fetchPersonas };
}

// =============================================================================
// usePersona - Fetch single persona by ID
// =============================================================================

interface UsePersonaReturn {
  persona: Persona | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePersona(id: string | undefined): UsePersonaReturn {
  const { getAccessToken } = useAuthContext();
  const [persona, setPersona] = useState<Persona | null>(null);
  const [isLoading, setIsLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);

  const fetchPersona = useCallback(async () => {
    if (!id) {
      setPersona(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/personas/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Persona not found');
        }
        throw new Error(`Failed to fetch persona: ${response.status}`);
      }

      const data: ApiPersonaResponse = await response.json();
      setPersona(fromApiPersona(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch persona');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, id]);

  useEffect(() => {
    fetchPersona();
  }, [fetchPersona]);

  return { persona, isLoading, error, refetch: fetchPersona };
}

// =============================================================================
// useCreatePersona - Create new persona
// =============================================================================

interface UseCreatePersonaReturn {
  createPersona: (persona: Partial<Persona> & { name: string }) => Promise<Persona>;
  isCreating: boolean;
  error: string | null;
}

export function useCreatePersona(): UseCreatePersonaReturn {
  const { getAccessToken } = useAuthContext();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPersona = useCallback(
    async (persona: Partial<Persona> & { name: string }): Promise<Persona> => {
      setIsCreating(true);
      setError(null);

      try {
        const token = await getAccessToken();
        const apiPayload: ApiPersonaCreate = toApiPersona(persona);

        const response = await fetch(`${API_BASE}/personas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to create persona: ${response.status}`);
        }

        const data: ApiPersonaResponse = await response.json();
        return fromApiPersona(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create persona';
        setError(message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [getAccessToken]
  );

  return { createPersona, isCreating, error };
}

// =============================================================================
// useUpdatePersona - Update existing persona
// =============================================================================

interface UseUpdatePersonaReturn {
  updatePersona: (id: string, updates: Partial<Persona>) => Promise<Persona>;
  isUpdating: boolean;
  error: string | null;
}

export function useUpdatePersona(): UseUpdatePersonaReturn {
  const { getAccessToken } = useAuthContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePersona = useCallback(
    async (id: string, updates: Partial<Persona>): Promise<Persona> => {
      setIsUpdating(true);
      setError(null);

      try {
        const token = await getAccessToken();

        // Build partial API update payload
        const apiPayload: ApiPersonaUpdate = {};

        if (updates.name !== undefined) {
          apiPayload.name = updates.name;
        }

        if (updates.worldview !== undefined) {
          apiPayload.worldview = {
            core_beliefs: updates.worldview.coreBeliefs,
            values: updates.worldview.values,
            audience: updates.worldview.audience,
            purpose: updates.worldview.purpose,
          };
        }

        if (updates.voice !== undefined) {
          apiPayload.voice = {
            traits: updates.voice.traits,
            description: updates.voice.description,
            example_phrases: updates.voice.examplePhrases,
            avoid_phrases: updates.voice.avoidPhrases,
          };
        }

        if (updates.decisions !== undefined) {
          apiPayload.dimensions = {
            claim_ownership: updates.decisions.claimOwnership,
            confidence: updates.decisions.confidence,
            call_to_action: updates.decisions.callToAction,
            vulnerability: updates.decisions.vulnerability,
            energy: updates.decisions.energy,
            formality: updates.decisions.formality,
            specificity: updates.decisions.specificity,
          };
        }

        const response = await fetch(`${API_BASE}/personas/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to update persona: ${response.status}`);
        }

        const data: ApiPersonaResponse = await response.json();
        return fromApiPersona(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update persona';
        setError(message);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [getAccessToken]
  );

  return { updatePersona, isUpdating, error };
}

// =============================================================================
// useDeletePersona - Delete persona
// =============================================================================

interface UseDeletePersonaReturn {
  deletePersona: (id: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export function useDeletePersona(): UseDeletePersonaReturn {
  const { getAccessToken } = useAuthContext();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deletePersona = useCallback(
    async (id: string): Promise<void> => {
      setIsDeleting(true);
      setError(null);

      try {
        const token = await getAccessToken();

        const response = await fetch(`${API_BASE}/personas/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to delete persona: ${response.status}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete persona';
        setError(message);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [getAccessToken]
  );

  return { deletePersona, isDeleting, error };
}
